/**
 * StatRow.jsx
 * The five figures the screen opens with.
 *
 * Each tile is a label, a value, and — only where one can honestly be
 * calculated — a movement against a named period. A tile with nothing to
 * compare against says what it is instead of inventing a percentage.
 *
 * Dependencies: helpers.js, components/Icons.jsx, utils/time.js
 */

import { Calendar, Chart, Check, Layers, Database, TrendUp, TrendDown } from '../Icons.jsx';
import { change, compact, direction, full, percent, pounds } from './helpers.js';
import { formatLondon } from '../../utils/time.js';

/**
 * One tile.
 *
 * `delta` is a signed percentage or null; `upIsGood` decides which way green
 * points, because spending 18% more is not the same kind of news as reaching
 * 18% more people.
 */
function Stat({ tile, label, value, delta = null, upIsGood = true, note, onClick }) {
  const dir = direction(delta, upIsGood);
  const Arrow = delta > 0 ? TrendUp : TrendDown;

  return (
    <button className="stat" onClick={onClick} type="button">
      <span className={`tile tile--sm ${tile.className ?? ''}`}>
        <tile.Icon size={18} />
      </span>
      <span className="stat__label">{label}</span>
      <span className="stat__value">{value}</span>
      <span className="stat__foot">
        {dir !== 'flat' && delta !== null && (
          <span className={`stat__delta stat__delta--${dir}`}>
            <Arrow size={14} />
            {delta > 0 ? '+' : ''}
            {delta.toFixed(0)}%
          </span>
        )}
        <span className="stat__note">{note}</span>
      </span>
    </button>
  );
}

export default function StatRow({ data, days, onNavigate }) {
  const perf = data.performance ?? {};
  const current = perf.current ?? {};
  const previous = perf.previous ?? {};
  const stats = data.stats ?? {};
  const spend = stats.spend ?? {};

  const window = `vs previous ${days} days`;

  return (
    <div className="stat-row">
      <Stat
        tile={{ Icon: Calendar }}
        label="Scheduled posts"
        value={full(stats.scheduled)}
        note={
          stats.nextOut ? `Next out ${formatLondon(stats.nextOut)}` : 'Nothing in the queue'
        }
        onClick={() => onNavigate('/calendar')}
      />

      <Stat
        tile={{ Icon: Check, className: 'tile--ok' }}
        label="Published"
        value={full(current.published)}
        delta={change(current.published, previous.published)}
        note={`Last ${days} days`}
        onClick={() => onNavigate('/analytics')}
      />

      <Stat
        tile={{ Icon: Chart }}
        label="Engagement rate"
        value={percent(current.rate)}
        delta={change(current.rate, previous.rate)}
        note={
          current.rate === null
            ? 'No figures collected yet'
            : `${compact(current.engagements)} of ${compact(current.impressions)} · ${window}`
        }
        onClick={() => onNavigate('/analytics')}
      />

      <Stat
        tile={{ Icon: Layers, className: data.drafts > 0 ? 'tile--warn' : '' }}
        label="Awaiting approval"
        value={full(data.drafts)}
        note={data.drafts > 0 ? 'Drafts needing a decision' : 'Nothing waiting'}
        onClick={() => onNavigate('/drafts')}
      />

      <Stat
        tile={{ Icon: Database }}
        label="AI spend this month"
        value={pounds(spend.pence)}
        delta={change(spend.pence, spend.previousPence)}
        upIsGood={false}
        note={`${full(spend.runs)} generations · vs last month`}
        onClick={() => onNavigate('/costs')}
      />
    </div>
  );
}
